import { createFileRoute, useNavigate } from '@tanstack/react-router';
import { defineFormPage, useFormPage } from '@repo/form';
import { FormActions } from '@repo/web-form';
import { useToast } from '../../../components/ui/Toast';
import { z } from 'zod';
import Decimal from 'decimal.js';
import { useCreateTemplate } from '../../../features/schemes/network/templates.api';
import { TemplateFormFields } from '../../../features/schemes/components/TemplateFormFields';
import { FormPageLayout } from '../../../components/layout/FormPageLayout';

export const Route = createFileRoute('/_auth/templates/new')({
  component: TemplateCreate,
});

const templateValidator = z.object({
  name: z.string().min(1, 'Name is required'),
  chitValue: z.number().min(1000, 'Minimum chit value is 1,000').multipleOf(1000, 'Must be a multiple of 1,000'),
  subscriberCount: z.number().min(5, 'Minimum 5 subscribers').max(50, 'Maximum 50 subscribers'),
  durationCycles: z.number().int().positive('Duration must be at least 1 cycle'),
  cycleFrequency: z.enum(['MONTHLY', 'FORTNIGHTLY', 'WEEKLY']),
  commissionRatePct: z.number().min(0).max(7, 'Commission cannot exceed 7% (statutory ceiling)'),
  maxBidDiscountRatePct: z.number().min(0).max(40, 'Max bid discount cannot exceed 40% (statutory ceiling)'),
  auctionType: z.enum(['OPEN', 'CLOSED']),
  hasForemanTicket: z.boolean(),
  foremanTicketIsPaying: z.boolean(),
  fmPrDue: z.number().int().min(0),
  requiresSurety: z.boolean(),
  maxSuretiesPerSubscriber: z.number().int().min(0).max(5),
  tdsApplicable: z.boolean(),
  gstApplicable: z.boolean(),
});

type FormValues = z.infer<typeof templateValidator>;

export const templateFormConfig = defineFormPage<FormValues>({
  resource: 'templates',
  defaultValues: {
    name: '',
    chitValue: 100000,
    subscriberCount: 20,
    durationCycles: 20,
    cycleFrequency: 'MONTHLY' as const,
    commissionRatePct: 4,
    maxBidDiscountRatePct: 30,
    auctionType: 'OPEN' as const,
    hasForemanTicket: false,
    foremanTicketIsPaying: false,
    fmPrDue: 0,
    requiresSurety: true,
    maxSuretiesPerSubscriber: 2,
    tdsApplicable: false,
    gstApplicable: true,
  },
  schema: templateValidator,
});

function TemplateCreate() {
  const navigate = useNavigate();
  const createMutation = useCreateTemplate();
  const { toast } = useToast();

  const {
    form,
    isSubmitting,
    handleSubmit,
    fieldError: fe,
  } = useFormPage(templateFormConfig, createMutation, {
    onBeforeSubmit: (value) => ({
      name: value.name,
      groupType: 'CASH' as const,
      // rupees -> paise; percent -> fraction
      chitValue: new Decimal(value.chitValue).mul(100).toNumber(),
      subscriberCount: value.subscriberCount,
      durationCycles: value.durationCycles,
      cycleFrequency: value.cycleFrequency,
      commissionRate: new Decimal(value.commissionRatePct).div(100).toNumber(),
      maxBidDiscountRate: new Decimal(value.maxBidDiscountRatePct).div(100).toNumber(),
      auctionType: value.auctionType,
      hasForemanTicket: value.hasForemanTicket,
      foremanTicketIsPaying: value.hasForemanTicket ? value.foremanTicketIsPaying : false,
      ...(value.hasForemanTicket && value.foremanTicketIsPaying && value.fmPrDue > 0
        ? { fmPrDue: value.fmPrDue }
        : {}),
      requiresSurety: value.requiresSurety,
      maxSuretiesPerSubscriber: value.maxSuretiesPerSubscriber,
      tdsApplicable: value.tdsApplicable,
      gstApplicable: value.gstApplicable,
    }),
    onSuccess: () => {
      toast({ type: 'success', title: 'Success', description: 'Template created as DRAFT. Activate it to allow group creation.' });
      navigate({ to: '/templates' });
    },
    onError: (err: { message?: string }) => {
      toast({ type: 'error', title: 'Error', description: err?.message || 'Failed to create template.' });
    },
  });

  return (
    <FormPageLayout
      title="New Scheme Template"
      subtitle="Define the reusable terms for a chit scheme. Groups snapshot these terms at creation — editing the template later never changes a running group."
      backTo="/templates"
      variant="plain"
    >
      <form
        onSubmit={handleSubmit}
        className="sm:bg-surface-1 sm:border sm:border-border sm:shadow-sm sm:rounded-[14px] sm:p-6 lg:p-8"
      >
        <TemplateFormFields form={form} fe={fe} isSubmitting={isSubmitting} validator={templateValidator} />

        <FormActions
          onCancel={() => navigate({ to: '/templates' })}
          isSubmitting={isSubmitting}
          submitLabel="Create Template"
        />
      </form>
    </FormPageLayout>
  );
}
